import { PROJECT_CATEGORY_ID } from "@/constants/utils.constants";
import { AppContext } from "@/services/AppContext/appContext";
import { Tag } from "@/types";
import { useCallback, useContext, useMemo } from "react";

export const useTags = () => {
  const { userTags } = useContext(AppContext);

  const getTagById = useCallback(
    (id: number | string): Tag | undefined => {
      try {
        if (typeof id === "string") {
          id = Number(id); // Using Number to convert string to number
        }

        if (isNaN(id)) {
          throw new Error("Invalid ID: " + id);
        }

        const tag = userTags
          .flatMap((category) => category.tags)
          .find((tag) => tag.id === id);
        if (!tag) {
          console.error("Tag not found with ID:", id);
          return undefined;
        }

        return tag;
      } catch (error) {
        console.error("Error fetching tag by ID: ", error);
        return undefined;
      }
    },
    [userTags]
  );

  const getTagsByCategoryID = useCallback(
    (categoryId: number): Tag[] => {
      try {
        const category = userTags.find((cat) => cat.id === categoryId);
        if (!category) {
          console.error("Category not found with ID:", categoryId);
          return [];
        }
        return category.tags;
      } catch (error) {
        console.error("Error fetching tags by category ID: ", error);
        return [];
      }
    },
    [userTags]
  );

  const getTagsNotInCategoryID = useCallback(
    (categoryId: number): Tag[] => {
      try {
        const allTags = userTags
          .filter((cat) => cat.id !== categoryId)
          .flatMap((cat) => cat.tags);
        return allTags;
      } catch (error) {
        console.error("Error fetching tags not in category ID: ", error);
        return [];
      }
    },
    [userTags]
  );

  const getTagsSplitProjectAndNon = useCallback((): {
    projectTags: Tag[];
    other: Tag[];
  } => {
    try {
      //  separate the tags into project and non-project tags

      const projectTags = getTagsByCategoryID(PROJECT_CATEGORY_ID);
      const other = getTagsNotInCategoryID(PROJECT_CATEGORY_ID);
      if (!projectTags || !other) {
        console.error("Tags not found in the specified categories.");
        return {
          projectTags: [],
          other: [],
        };
      }
      return {
        projectTags,
        other,
      };
    } catch (error) {
      console.error("Error getTagsSplitProjectAndNon: ", error);
      return {
        projectTags: [],
        other: [],
      };
    }
  }, [getTagsByCategoryID, getTagsNotInCategoryID]);

  const findProjectTags = useCallback(
    (currentTags: Tag[]): Tag[] => {
      try {
        // find the tags that are in the project category from the current tags
        const allProjectTags = getTagsByCategoryID(PROJECT_CATEGORY_ID);
        const projectTags = currentTags.filter((tag) =>
          allProjectTags.some((projectTag) => projectTag.id === tag.id)
        );
        if (!projectTags) {
          console.log("No project tags found in the current tags.");
          return [];
        }

        return projectTags;
      } catch (error) {
        console.error("Error findProjectTags: ", error);
        return [];
      }
    },
    [getTagsByCategoryID]
  );

  const findNonProjectTags = useCallback(
    (currentTags: Tag[]): Tag[] => {
      try {
        // find the tags that are not in the project category from the current tags
        const allNonProjectTags = getTagsNotInCategoryID(PROJECT_CATEGORY_ID);
        const nonProjectTags = currentTags.filter((tag) =>
          allNonProjectTags.some((nonProjectTag) => nonProjectTag.id === tag.id)
        );
        if (!nonProjectTags) {
          console.log("No non-project tags found in the current tags.");
          return [];
        }
        return nonProjectTags;
      } catch (error) {
        console.error("Error findNonProjectTags: ", error);
        return [];
      }
    },
    [getTagsNotInCategoryID]
  );

  // useMemo ensures the returned object is memoized and only recreated when dependencies change
  return useMemo(
    () => ({
      getTagById,
      getTagsSplitProjectAndNon,
      getTagsByCategoryID,
      getTagsNotInCategoryID,
      findProjectTags,
      findNonProjectTags,
    }),
    [
      getTagById,
      getTagsSplitProjectAndNon,
      getTagsByCategoryID,
      getTagsNotInCategoryID,
      findProjectTags,
      findNonProjectTags,
    ]
  );
};
